/**
 * FriendsGridSkeleton Component
 * Skeleton grid matching FriendsGrid layout exactly
 * Uses FlashList structure to match real grid behavior
 */

import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { FLASHLIST_ANDROID_OVERRIDE_PROPS } from '../../../src/shared/constants/flashList.constants';
import { FriendCardSkeleton } from '../FriendCard/FriendCardSkeleton';
import { friendsGridStyles } from './FriendsGrid.styles';

interface FriendsGridSkeletonProps {
  numColumns: number;
  screenWidth: number;
  /** When set, use this width (content area) to match FriendsGrid alignment. */
  contentWidth?: number;
  /** Test ID for testing */
  testID?: string;
}

export const FriendsGridSkeleton: React.FC<FriendsGridSkeletonProps> = ({
  numColumns,
  screenWidth,
  contentWidth,
  testID,
}) => {
  const listWidth = contentWidth ?? screenWidth;

  // Calculate card width matching FriendsGrid: when contentWidth set, fill row; else cap at 200
  const { cardWidth } = useMemo(() => {
    const gap = 16;
    const maxCardWidthPerColumn = 200;

    const availableWidth = listWidth;
    const totalGaps = (numColumns - 1) * gap;
    const calculatedWidth = (availableWidth - totalGaps) / numColumns;
    const width =
      contentWidth != null
        ? calculatedWidth
        : Math.min(calculatedWidth, maxCardWidthPerColumn);

    return { cardWidth: width };
  }, [listWidth, numColumns, contentWidth]);

  // Memoize card container base styles (matching FriendsGrid)
  const cardContainerBaseStyle = useMemo(
    () => ({
      width: cardWidth,
      marginBottom: 16,
    }),
    [cardWidth]
  );

  // Show 6 skeleton cards (2-3 rows depending on columns)
  const skeletonData = Array.from({ length: 6 }, (_, i) => ({ id: `skeleton-${i}` }));

  return (
    <FlashList
      overrideProps={FLASHLIST_ANDROID_OVERRIDE_PROPS}
      data={skeletonData}
      numColumns={numColumns}
      key={numColumns} // Force re-render on column change (matching FriendsGrid)
      testID={testID || 'friends-grid-skeleton'}
      renderItem={({ index }) => {
        // Calculate position in grid (matching FriendsGrid logic)
        const cardPosition = index;
        const isLastInRow = (cardPosition + 1) % numColumns === 0;

        return (
          <View
            style={[
              cardContainerBaseStyle,
              isLastInRow ? friendsGridStyles.cardContainerLastInRow : friendsGridStyles.cardContainerNotLastInRow,
            ]}
          >
            <FriendCardSkeleton cardWidth={cardWidth} testID={`${testID || 'friend-card-skeleton'}-${index}`} />
          </View>
        );
      }}
      style={StyleSheet.flatten([friendsGridStyles.flashList, { width: listWidth }])}
      showsVerticalScrollIndicator={false}
      keyExtractor={(item) => item.id}
    />
  );
};

